import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';
import MaterialSymbol from '../common/MaterialSymbol';
import LearningPathCard, { CourseProgressInfo } from './LearningPathCard';
import { learningPathService, LearningPath, LearningPathEnrollment } from '../../services/learningPathService';

interface LearningPathListProps {
    onPathSelect?: (pathId: number) => void;
    showOnlyEnrolled?: boolean;
}

/**
 * Extract path ID from enrollment's learningPath field
 * Handles both object format and IRI string format
 */
function extractPathId(learningPath: any): number | null {
    if (!learningPath) return null;

    // If it's an object with id property
    if (typeof learningPath === 'object' && learningPath.id) {
        return learningPath.id;
    }

    // If it's a string IRI like "/api/learning_paths/123"
    if (typeof learningPath === 'string') {
        const match = learningPath.match(/\/learning_paths\/(\d+)/);
        if (match) {
            return parseInt(match[1], 10);
        }
    }

    return null;
}

/** Extract course ID from a course-enrollment's course field (object, number or IRI). */
function extractCourseId(course: any): number | null {
    if (!course) return null;
    if (typeof course === 'object' && typeof course.id === 'number') return course.id;
    if (typeof course === 'number') return course;
    if (typeof course === 'string') {
        const match = course.match(/\/courses\/(\d+)/);
        if (match) return parseInt(match[1], 10);
    }
    return null;
}

const LearningPathList: React.FC<LearningPathListProps> = ({ onPathSelect, showOnlyEnrolled = false }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [paths, setPaths] = useState<LearningPath[]>([]);
    const [enrollments, setEnrollments] = useState<Map<number, LearningPathEnrollment>>(new Map());
    const [courseProgress, setCourseProgress] = useState<Map<number, CourseProgressInfo> | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
    const [userDeptId, setUserDeptId] = useState<number | null>(null);
    const [userGroupIds, setUserGroupIds] = useState<number[]>([]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [pathsData, enrollmentsData] = await Promise.all([
                learningPathService.getLearningPaths(),
                learningPathService.getMyEnrollments(),
            ]);

            // Load user's department/groups from SSO to check allowed enrollment
            try {
                const token = localStorage.getItem('token');
                const ssoUrl = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';
                const meRes = await fetch(`${ssoUrl}/api/me`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                });
                if (meRes.ok) {
                    const meData = await meRes.json();
                    // department is {id: X, name: "..."} or null
                    if (meData.department && meData.department.id) {
                        setUserDeptId(Number(meData.department.id));
                    }
                    // userGroups is [{id: X, name: "..."}, ...]
                    if (meData.userGroups && Array.isArray(meData.userGroups)) {
                        setUserGroupIds(meData.userGroups.map((g: any) => Number(g.id)).filter(Boolean));
                    }
                }
            } catch {}

            setPaths(pathsData);

            // Create a map of path ID to enrollment
            const enrollmentMap = new Map<number, LearningPathEnrollment>();
            for (const enrollment of enrollmentsData) {
                const pathId = extractPathId(enrollment.learningPath);
                if (pathId) {
                    enrollmentMap.set(pathId, enrollment);
                }
            }
            setEnrollments(enrollmentMap);

            // Kunden-Feedback (bindend): Der Balken auf der Karte zeigt den
            // Fortschritt im AKTUELLEN Kurs. Dafür einmalig die eigenen
            // Kurs-Einschreibungen (Status + Fortschritt je Kurs) laden —
            // der Provider filtert automatisch auf den eigenen User.
            if (enrollmentMap.size > 0) {
                try {
                    const { data: myCourseEnrollments } = await dataProvider.getList('course_enrollments', {
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'id', order: 'DESC' },
                        filter: {},
                    });
                    const progressMap = new Map<number, CourseProgressInfo>();
                    for (const courseEnrollment of myCourseEnrollments as any[]) {
                        const courseId = extractCourseId(courseEnrollment.course);
                        if (courseId === null || progressMap.has(courseId)) continue;
                        progressMap.set(courseId, {
                            status: courseEnrollment.status || 'enrolled',
                            progress: parseFloat(courseEnrollment.progressPercentage ?? '0') || 0,
                        });
                    }
                    setCourseProgress(progressMap);
                } catch {
                    // Degradiert sauber: ohne Kurs-Fortschritt zeigt die Karte
                    // den Pfad-Fortschritt.
                    setCourseProgress(undefined);
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load learning paths');
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async (pathId: number) => {
        try {
            setEnrolling(pathId);
            const enrollment = await learningPathService.enrollInPath(pathId);

            // Update enrollments map
            setEnrollments(prev => {
                const newMap = new Map(prev);
                newMap.set(pathId, enrollment);
                return newMap;
            });

            if (enrollment.status === 'pending_approval') {
                notify('Ihre Einschreibungsanfrage wurde gesendet.', { type: 'info' });
            } else {
                notify(translate('learningPaths.enrollSuccess', { _: 'Erfolgreich eingeschrieben!' }), { type: 'success' });
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Enrollment failed';
            if (msg.includes('not allowed')) {
                notify('Selbsteinschreibung ist für diesen Lernpfad nicht erlaubt.', { type: 'warning' });
            } else {
                notify(msg, { type: 'error' });
            }
        } finally {
            setEnrolling(null);
        }
    };

    // Check if user is in an allowed department/group for a specific path
    const isUserAllowed = (path: LearningPath): boolean => {
        const allowedDepts = path.allowedDepartments || [];
        const allowedGroups = path.allowedGroups || [];
        if (allowedDepts.length === 0 && allowedGroups.length === 0) return false;
        if (userDeptId && allowedDepts.includes(userDeptId)) return true;
        if (userGroupIds.length > 0 && allowedGroups.some(g => userGroupIds.includes(g))) return true;
        return false;
    };

    // Compute effective enrollment mode per path (considering allowed depts/groups)
    const getEffectiveMode = (path: LearningPath): string => {
        const mode = path.enrollmentMode || 'self';
        if (mode === 'self') return 'self';
        if (isUserAllowed(path)) return 'self'; // bypass for allowed dept/group
        return mode;
    };

    const filteredPaths = paths.filter(path => {
        // Filter to only enrolled paths if requested
        if (showOnlyEnrolled && !enrollments.has(path.id)) {
            return false;
        }

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            if (!path.title.toLowerCase().includes(query) &&
                !path.description?.toLowerCase().includes(query)) {
                return false;
            }
        }

        // Difficulty filter
        if (difficultyFilter !== 'all' && path.difficulty !== difficultyFilter) {
            return false;
        }

        return true;
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <div>
            {/* Toolbar: Suche + Schwierigkeits-Filter */}
            <div className="lp-toolbar">
                <div className="lp-search">
                    <MaterialSymbol name="search" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={translate('learningPaths.search', { _: 'Lernpfad suchen...' })}
                        aria-label={translate('learningPaths.search', { _: 'Lernpfad suchen...' })}
                        autoComplete="off"
                    />
                </div>
                <div className="lp-select">
                    <select
                        value={difficultyFilter}
                        onChange={(e) => setDifficultyFilter(e.target.value)}
                        aria-label={translate('learningPaths.difficulty', { _: 'Schwierigkeit' })}
                    >
                        <option value="all">{translate('learningPaths.allDifficulties', { _: 'Alle' })}</option>
                        <option value="beginner">{translate('learningPaths.beginner', { _: 'Einstieg' })}</option>
                        <option value="intermediate">{translate('learningPaths.intermediate', { _: 'Vertiefung' })}</option>
                        <option value="advanced">{translate('learningPaths.advanced', { _: 'Spezialisierung' })}</option>
                    </select>
                    <MaterialSymbol name="expand_more" className="chev" />
                </div>
            </div>

            <p className="lp-count">
                {filteredPaths.length === 1 ? '1 Lernpfad' : `${filteredPaths.length} Lernpfade`}
            </p>

            {/* Path grid */}
            {filteredPaths.length === 0 ? (
                <p className="lp-empty">
                    {showOnlyEnrolled
                        ? translate('learningPaths.noEnrollments', { _: 'Du bist noch in keinem Lernpfad eingeschrieben' })
                        : translate('learningPaths.noPathsFound', { _: 'Keine Lernpfade gefunden' })
                    }
                </p>
            ) : (
                <section className="lp-grid">
                    {filteredPaths.map((path) => (
                        <LearningPathCard
                            key={path.id}
                            path={{ ...path, enrollmentMode: getEffectiveMode(path) as any }}
                            enrollment={enrollments.get(path.id)}
                            onEnroll={handleEnroll}
                            onView={onPathSelect}
                            loading={enrolling === path.id}
                            courseProgress={courseProgress}
                        />
                    ))}
                </section>
            )}
        </div>
    );
};

export default LearningPathList;
